import { useState } from "react";
import { ShieldAlert, Check, X, ChevronDown, ChevronUp } from "lucide-react";

interface DisclaimerGateProps {
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  onAccept: () => void;
  onDecline: () => void;
}

const STORAGE_KEY = "disclaimer_accepted_v1";

export function isDisclaimerAccepted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function acceptDisclaimer() {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {}
}

export function DisclaimerGate({ t, dir, onAccept, onDecline }: DisclaimerGateProps) {
  const [expanded, setExpanded] = useState(false);

  const sections = [
    { title: t("disclaimerSec1Title"), body: t("disclaimerSec1Body") },
    { title: t("disclaimerSec2Title"), body: t("disclaimerSec2Body") },
    { title: t("disclaimerSec3Title"), body: t("disclaimerSec3Body") },
    { title: t("disclaimerSec4Title"), body: t("disclaimerSec4Body") },
    { title: t("disclaimerSec5Title"), body: t("disclaimerSec5Body") },
    { title: t("disclaimerSec6Title"), body: t("disclaimerSec6Body") },
    { title: t("disclaimerSec7Title"), body: t("disclaimerSec7Body") },
    { title: t("disclaimerSec8Title"), body: t("disclaimerSec8Body") },
  ];

  const handleAccept = () => {
    acceptDisclaimer();
    onAccept();
  };

  const handleDecline = () => {
    onDecline();
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" dir={dir}>
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0A2240] to-[#0D1B2E] px-6 py-6 flex flex-col items-center gap-3 shrink-0">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-lg font-bold text-white text-center">{t("disclaimerTitle")}</h2>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          <p className="text-sm text-stone-600 dark:text-slate-300 leading-relaxed text-center font-medium">
            {t("disclaimerIntro")}
          </p>

          {/* First section always visible */}
          <div className="rounded-2xl border border-stone-200 dark:border-slate-700 p-3.5 space-y-1.5">
            <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{sections[0].title}</p>
            <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed">{sections[0].body}</p>
          </div>

          {/* Collapsible remaining sections */}
          <button
            onClick={() => setExpanded(v => !v)}
            className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-600 dark:text-slate-300 text-xs font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors"
          >
            <span>{t("consentViewFull")}</span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {expanded && (
            <div className="space-y-2.5 animate-in fade-in duration-200">
              {sections.slice(1).map((s, i) => (
                <div key={i} className="rounded-2xl border border-stone-200 dark:border-slate-700 p-3.5 space-y-1.5">
                  <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{s.title}</p>
                  <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          )}

          <p className="text-[11px] text-stone-500 dark:text-slate-400 leading-relaxed text-center pt-1">
            {t("disclaimerAcceptance")}
          </p>
        </div>

        {/* Footer buttons */}
        <div className="px-5 pb-5 pt-3 shrink-0 border-t border-stone-100 dark:border-slate-800 space-y-2.5">
          <button
            onClick={handleAccept}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            {t("disclaimerAccept")}
          </button>
          <button
            onClick={handleDecline}
            className="w-full py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-500 dark:text-slate-400 font-semibold text-sm hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            {t("disclaimerDecline")}
          </button>
        </div>
      </div>
    </div>
  );
}
